"use client"

/**
 * Client notes in the appointment window — the owner's pick "A" (2026-10-18): their own section, the same
 * notes as the client card in the dashboard (boona docs/codebase/attachments/README.md §1). Staff only.
 * A note can carry photos and videos (one chip each, the dark viewer); files picked while writing go up
 * at once and join the note when it is saved; a saved note's author (or whoever may edit others' notes)
 * adds more to it.
 */
import * as React from "react"
import { Check, Loader2, Lock } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { useAppointmentModal } from "@/contexts/AppointmentModalContext"
import {
  addClientNote, deleteAttachment, listClientNotes, refusal, type ApiNote,
} from "@/lib/services/attachmentsService"
import { AttachControls, useFileDrop } from "@/components/custom/attachments/AttachControls"
import { AttachmentChip } from "@/components/custom/attachments/AttachmentChip"
import { AttachmentViewer } from "@/components/custom/attachments/AttachmentViewer"
import { PendingUploads } from "@/components/custom/attachments/PendingUploads"
import { localWhen, toItem } from "@/components/custom/attachments/mapItem"
import type { AttachmentItem } from "@/components/custom/attachments/types"
import { useAttachmentUploader } from "@/components/custom/attachments/useAttachmentUploader"
import { cn } from "@/lib/utils"

type Note = Omit<ApiNote, "attachments"> & { items: AttachmentItem[] }

/** "+ photo" on a saved note: its own uploader, files join the note at once */
function AddToNote({ noteId, count, onAdded }: { noteId: number; count: number; onAdded: (item: AttachmentItem) => void }) {
  const uploader = useAttachmentUploader({ kind: "note", noteId }, { mode: "attach", existing: count, onUploaded: onAdded })
  return (
    <div className="mt-1.5">
      <AttachControls onFiles={(f) => void uploader.add(f)} />
      <PendingUploads items={uploader.pending} onRemove={uploader.remove} />
    </div>
  )
}

export function ClientNotesSection({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation()
  const { appointment } = useAppointmentModal()
  const clientId = Number(appointment?.client?.id) || 0
  const [notes, setNotes] = React.useState<Note[]>([])
  const [canAdd, setCanAdd] = React.useState(false)
  const [state, setState] = React.useState<"loading" | "ready" | "denied" | "error">("loading")
  const [text, setText] = React.useState("")
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [viewer, setViewer] = React.useState<{ note: number; index: number } | null>(null)
  const [adding, setAdding] = React.useState<number | null>(null)

  const load = React.useCallback(async () => {
    if (!clientId) return
    try {
      const r = await listClientNotes(clientId)
      setNotes(r.notes.map(({ attachments, ...n }) => ({ ...n, items: attachments.map(toItem) })))
      setCanAdd(r.can_add)
      setState("ready")
    } catch (err) {
      setState(refusal(err).status === 403 ? "denied" : "error")
    }
  }, [clientId])
  React.useEffect(() => { setState("loading"); void load() }, [load])

  const converting = notes.some((n) => n.items.some((i) => i.processing))
  React.useEffect(() => {
    if (!converting) return
    const id = window.setInterval(() => { void load() }, 5000)
    return () => window.clearInterval(id)
  }, [converting, load])

  const draft = useAttachmentUploader(clientId ? { kind: "note_draft", clientId } : null, { mode: "draft", existing: 0 })
  const drop = useFileDrop((f) => void draft.add(f), !canAdd)

  const save = async () => {
    const ids = draft.pending.filter((u) => u.serverId && (u.state === "ready" || u.state === "processing")).map((u) => u.serverId!)
    if (!text.trim() && !ids.length) return
    setSaving(true)
    setError(null)
    try {
      await addClientNote(clientId, text.trim(), draft.takeReady())
      setText("")
      await load()
    } catch (err) {
      setError(refusal(err).message || t("attachments.err.note_failed", "Couldn't save the note — try again."))
    } finally {
      setSaving(false)
    }
  }

  const open = viewer ? notes.find((n) => n.id === viewer.note) : undefined

  if (!clientId) return null
  return (
    <div className={compact ? "" : "flex-1 overflow-y-auto"}>
      <div className={compact ? "space-y-4" : "max-w-3xl space-y-5 p-6"}>
        <div>
          {!compact && <h2 className="text-base font-medium">{t("appointment.sidebar.client_notes", "Client notes")}</h2>}
          <p className="mt-0.5 text-xs text-gray-500">{t("attachments.notes_private", "Staff only — never shown to the client.")}</p>
        </div>

        {state === "denied" && (
          <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-3 text-sm text-gray-600">
            <Lock className="h-4 w-4" />{t("attachments.notes_denied", "You don't have access to the client's notes.")}
          </div>
        )}
        {state === "error" && <div className="text-sm text-red-600">{t("attachments.notes_error", "Couldn't load the notes.")}</div>}
        {state === "loading" && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}

        {state === "ready" && canAdd && (
          <div {...drop.props} className={cn("rounded-lg border p-3", drop.over && "outline-dashed outline-2 outline-offset-2 outline-sky-400")}>
            <Textarea className="min-h-[70px] border-gray-300 text-base md:text-sm"
                      placeholder={t("attachments.note_placeholder", "Add a note: a formula, a preference, what you talked about…")}
                      value={text} onChange={(e) => setText(e.target.value)} maxLength={5000} />
            <PendingUploads items={draft.pending} onRemove={draft.remove} />
            {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <AttachControls onFiles={(f) => void draft.add(f)} />
              <Button type="button" size="sm" disabled={saving || draft.busy || (!text.trim() && !draft.pending.some((u) => u.serverId))}
                      onClick={() => void save()}>
                {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
                {t("attachments.save_note", "Save note")}
              </Button>
            </div>
          </div>
        )}

        {state === "ready" && (
          <div className="divide-y">
            {notes.length === 0 && (
              <div className="py-6 text-sm text-gray-400">{t("attachments.no_notes", "No notes yet — preferences, formulas, what you talked about stay here.")}</div>
            )}
            {notes.map((n) => (
              <div key={n.id} className="py-3">
                <div className="text-xs text-gray-400">
                  <b className="font-semibold text-gray-600">{n.author}</b> · {localWhen(n.noted_at, "dd.MM.yyyy HH:mm")}
                </div>
                {n.text && <p className="mt-1 whitespace-pre-line text-sm">{n.text}</p>}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <AttachmentChip items={n.items} onOpen={(i) => setViewer({ note: n.id, index: i })} />
                  {n.can_edit && adding !== n.id && n.items.length < 10 && (
                    <button type="button" className="text-xs text-sky-700 hover:underline" onClick={() => setAdding(n.id)}>
                      {t("attachments.add_to_note", "+ photo or video")}
                    </button>
                  )}
                </div>
                {adding === n.id && (
                  <AddToNote noteId={n.id} count={n.items.length}
                             onAdded={(item) => setNotes((all) => all.map((x) => (x.id === n.id ? { ...x, items: [...x.items, item] } : x)))} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <AttachmentViewer
        items={open?.items ?? []}
        index={open ? viewer!.index : null}
        onIndexChange={(i) => setViewer((v) => (i === null || !v ? null : { ...v, index: i }))}
        onDelete={open && open.items[viewer!.index]?.canDelete ? async (item) => {
          if (!window.confirm(t("attachments.delete_confirm", "Delete this file? It can't be undone."))) return
          await deleteAttachment(Number(item.id))
          const left = open.items.filter((i) => i.id !== item.id)
          setNotes((all) => all.map((x) => (x.id === open.id ? { ...x, items: left } : x)))
          setViewer(left.length ? { note: open.id, index: Math.min(viewer!.index, left.length - 1) } : null)
        } : undefined}
      />
    </div>
  )
}
